import CIndexedDB from '../../CIndexedDB';
import { cCHAVE_IDENTIFICADOR_ABA } from '../../CSessao';
import CSessaoAtual from '@/services/api/security/CSessaoAtual';
import CServidorTekMonitor from '@/services/tek-monitor/CServidorTekMonitor';
import CPreferenciaCredencialAcessoERP from './classes/CPreferenciaCredencialAcessoERP';
import CPreferenciaCredencialAcessoAPI from './classes/CPreferenciaCredencialAcessoAPI';

/**
 * Chaves permitidas no store de Security.
 * Padronizadas para evitar erros de runtime.
 */
export type TChavePreferenciasSecurity =
  | 'CredenciaisERP' // Global
  | 'CredencialAPI' // Global
  | 'CredencialERP' // Por Aba
  | 'CredencialAPI' // Por Aba
  | 'SessaoAtual' // Por Aba
  | 'ServidorAplicacao'; // Por Aba

export default class CPreferenciasSecurity extends CIndexedDB<TChavePreferenciasSecurity> {
  private readonly identificador: string;
  private abaOrigemDuplicada: string | null = null;
  static readonly storeName = 'Security';

  constructor(pIndexDB: IDBDatabase) {
    super(pIndexDB, CPreferenciasSecurity.storeName);

    // Recupera o ID da aba do sessionStorage (sobrevive a reloads e navegação manual)
    let lIdentificadorAba = sessionStorage.getItem(cCHAVE_IDENTIFICADOR_ABA) ?? '';

    // Se existe ID no session, mas o window.name está diferente, significa que a aba foi DUPLICADA (o session foi copiado, mas o name nasceu limpo).
    if (lIdentificadorAba && window.name !== lIdentificadorAba) {
      this.abaOrigemDuplicada = lIdentificadorAba;
      lIdentificadorAba = ''; // Anula para forçar a criação de uma sessão totalmente nova para esta aba
    }

    // Se não existir, é uma aba realmente nova (ou recém-duplicada). Cria o ID e salva.
    if (!lIdentificadorAba) {
      lIdentificadorAba = `aba_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      sessionStorage.setItem(cCHAVE_IDENTIFICADOR_ABA, lIdentificadorAba);
    }

    // Sincroniza o window.name para validações futuras (como F5 ou mudança manual de URL)
    window.name = lIdentificadorAba;

    this.identificador = lIdentificadorAba;
  }

  /**
   * Ao duplicar a página ele realiza a cópia de todos os dados para a nova aba duplicada
   */
  async sincronizarDadosAbaDuplicada(): Promise<void> {
    if (!this.abaOrigemDuplicada) return;

    try {
      // Formata manualmente as chaves da aba que foi clonada
      const lChaveSessaoAntiga: `${string}_${TChavePreferenciasSecurity}` = `${this.abaOrigemDuplicada}_SessaoAtual`;
      const lChaveServidorAntigo: `${string}_${TChavePreferenciasSecurity}` = `${this.abaOrigemDuplicada}_ServidorAplicacao`;

      const lSessaoAntiga = await this.recuperar<CSessaoAtual>(lChaveSessaoAntiga as TChavePreferenciasSecurity);
      const lServidorAntigo = await this.recuperar<CServidorTekMonitor>(
        lChaveServidorAntigo as TChavePreferenciasSecurity,
      );

      // Se existiam dados na aba velha, salva na aba nova usando o formatarChave padrão (que já usa o novo this.identificador)
      if (lSessaoAntiga) {
        await this.armazenar(this.formatarChave('SessaoAtual', false), lSessaoAntiga);
      }
      if (lServidorAntigo) {
        await this.armazenar(this.formatarChave('ServidorAplicacao', false), lServidorAntigo);
      }
    } catch (error) {
      console.error('[Security] Erro ao sincronizar aba duplicada:', error);
    } finally {
      // Limpa para garantir que rode apenas uma vez na vida útil da aba
      this.abaOrigemDuplicada = null;
    }
  }

  /**
   * Resolve o conflito de múltiplas abas injetando o ID da aba na chave do IndexedDB.
   */
  private formatarChave(pChave: TChavePreferenciasSecurity, pIsGlobal: boolean): string {
    return pIsGlobal ? pChave : `${this.identificador}_${pChave}`;
  }

  // --- CONFIGURAÇÃO DE INFRA (POR ABA) ---

  async armazenarServidorAplicacao(pServidor: CServidorTekMonitor): Promise<void> {
    await this.armazenar(this.formatarChave('ServidorAplicacao', false), pServidor);
  }

  async recuperarServidorAplicacao(): Promise<CServidorTekMonitor> {
    const lDados = await this.recuperar<CServidorTekMonitor>(this.formatarChave('ServidorAplicacao', false));
    return lDados ? new CServidorTekMonitor(lDados) : new CServidorTekMonitor();
  }

  // --- SESSÃO E AUTH ERP (POR ABA) ---

  async armazenarSessaoAtual(pSessao: CSessaoAtual): Promise<void> {
    await this.armazenar(this.formatarChave('SessaoAtual', false), pSessao);
  }

  async recuperarSessaoAtual(): Promise<CSessaoAtual> {
    const lDados = await this.recuperar<CSessaoAtual>(this.formatarChave('SessaoAtual', false));
    return lDados ? new CSessaoAtual(lDados) : new CSessaoAtual();
  }

  // --- HISTÓRICO DE ACESSOS (GLOBAL ENTRE TODAS AS ABAS) ---

  async armazenarCredencialERP(pCredencial: CPreferenciaCredencialAcessoERP): Promise<void> {
    await this.armazenar(this.formatarChave('CredencialERP', true), pCredencial);

    const lHistorico = await this.recuperarCredenciaisHistoricoERP();
    const lIndice = lHistorico.findIndex((item) => item.cliente.codigo === pCredencial.cliente.codigo);

    pCredencial.ultimoAcesso = new Date();
    if (lIndice === -1) lHistorico.push(pCredencial);
    else lHistorico[lIndice] = pCredencial;

    lHistorico.sort((a, b) => new Date(b.ultimoAcesso).getTime() - new Date(a.ultimoAcesso).getTime());
    await this.armazenar(this.formatarChave('CredenciaisERP', true), lHistorico);
  }

  async recuperarCredencialERP(): Promise<CPreferenciaCredencialAcessoERP> {
    const lDados = await this.recuperar<CPreferenciaCredencialAcessoERP>(this.formatarChave('CredencialERP', true));
    return lDados ? new CPreferenciaCredencialAcessoERP(lDados) : new CPreferenciaCredencialAcessoERP();
  }

  async recuperarCredenciaisHistoricoERP(): Promise<CPreferenciaCredencialAcessoERP[]> {
    return (await this.recuperar<CPreferenciaCredencialAcessoERP[]>(this.formatarChave('CredenciaisERP', true))) ?? [];
  }

  async armazenarCredencialAPI(pCredencial: CPreferenciaCredencialAcessoAPI): Promise<void> {
    await this.armazenar(this.formatarChave('CredencialAPI', true), pCredencial);
  }

  async recuperarCredencialAPI(): Promise<CPreferenciaCredencialAcessoAPI> {
    return (
      (await this.recuperar<CPreferenciaCredencialAcessoAPI>(this.formatarChave('CredencialAPI', true))) ??
      new CPreferenciaCredencialAcessoAPI()
    );
  }

  // --- LIMPEZA ---

  /**
   * Limpa todos os dados sensíveis da aba atual.
   */
  async limparSessaoAba(): Promise<void> {
    const chavesParaRemover: TChavePreferenciasSecurity[] = ['SessaoAtual', 'ServidorAplicacao'];

    const promises = chavesParaRemover.map((chave) => this.excluir(this.formatarChave(chave, false)));

    await Promise.all(promises);
  }

  /**
   * Remove sessões de abas que não foram atualizadas há mais de X horas.
   * @param pHorasLimite Tempo em horas para expiração (default 24h)
   */
  async realizarLimpezaSessoesInativas(pHorasLimite: number = 24): Promise<void> {
    try {
      const transaction = this.database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // getAll() recupera todos os objetos do Store de uma vez
      const request = store.getAll();

      request.onsuccess = () => {
        const todosRegistros = request.result;
        const agora = new Date().getTime();
        const limiteMs = pHorasLimite * 60 * 60 * 1000; // Converte horas para milissegundos

        todosRegistros.forEach((registro: any) => {
          // Identifica se a chave contém o prefixo de isolamento de aba
          const isChaveDeAba = registro.key.startsWith('aba_');

          if (isChaveDeAba) {
            // Pega o timestamp que você salvou no campo 'tempo'
            const timestampRegistro = registro.timestamp;

            // Se o tempo não existir ou for menor que o limite de expiração, deletamos
            if (timestampRegistro && agora - timestampRegistro > limiteMs) {
              console.log(`[GC Security] Removendo sessão expirada: ${registro.key}`);
              store.delete(registro.key);
            }
          }
        });
      };

      transaction.oncomplete = () => {
        //console.log(`[GC Security] Processo de limpeza concluído.`);
      };
    } catch (error) {
      console.error('[GC Security] Erro ao realizar limpeza:', error);
    }
  }
}

/**
Gualter: Exemplo para validar se a limpeza está funcionando corretamente

const storage = new (await import('@/services/storage/preferencias/security/CPreferenciasSecurity')).default();

// Cria um dado manualmente com tempo de 3 dias atrás (em milissegundos)
const tresDiasAtras = new Date().getTime() - (3 * 24 * 60 * 60 * 1000);

// Pega o banco e insere direto (simulando sua classe base)
const db = await storage.init();
const tx = db.transaction('Security', 'readwrite');
tx.objectStore('Security').put({
  key: 'aba_TESTE_LIMPEZA_SessaoAtual',
  timestamp: tresDiasAtras,
  dados: { teste: true }
});

// Roda a limpeza
await storage.realizarLimpezaSessoesInativas(1);
*/
